import type { PropsWithChildren, ReactNode } from "react";
import { useIntl } from "react-intl";
import { isLoggedIn } from "src/auth/utils/session";
import { border_radius } from "src/design-system/tokens/border";
import styled, { css } from "styled-components";
import Profile from "src/design-system/icons/Profile.svg?react";
import color from "src/design-system/tokens/color";
import { spacing } from "src/design-system/tokens/spacing";
import { tw } from "src/design-system/tw";
import { ClickAwayListener } from "../../../components/ClickAwayListener/ClickAwayListener";
import { Dropdown } from "../../../components/Dropdown/Dropdown";
import { Icon } from "../../../components/Icon/Icon";
import { Skeleton } from "../../../components/Skeleton/Skeleton";
import { UserCircle } from "../../../svg/UserCircle";
import { CloseButton } from "../CloseButton/CloseButton";
import { messages } from "./SignInButton.messages";

type SignInButtonProps = {
  text?: string;
  label?: string;
  handleClose?: () => void;
  handleClickButton: () => void;
  isDropdownOpen?: boolean;
  handleClickOffDropdown?: () => void;
  fullWidth?: boolean;
  variant?: "default" | "horizontalSearch";
  className?: string;
};

export function SignInButton(props: PropsWithChildren<SignInButtonProps>) {
  const { variant = "default" } = props;
  const isSignedIn = isLoggedIn();

  const text = props.text ?? (
    <Skeleton repeat={1} height={16} width={75} lightOnDark={!isSignedIn} />
  );

  return (
    <DropdownWrapper>
      <DropdownButton
        data-hj-suppress
        aria-label={props.label}
        onClick={props.handleClickButton}
        fullWidth={props.fullWidth}
        $variant={variant}
        $isSignedIn={isSignedIn}
      >
        {variant === "default" && <DefaultButtonContent text={text} />}
        {variant === "horizontalSearch" && (
          <TripsContextButtonContent isSignedIn={isSignedIn} />
        )}
      </DropdownButton>
      {props.isDropdownOpen && (
        <ClickAwayListener onClickAway={props.handleClickOffDropdown}>
          <StyledDropdown anchor="right">
            <CloseButtonWrapper>
              <CloseButton onClick={props.handleClose} />
            </CloseButtonWrapper>
            {props.children}
          </StyledDropdown>
        </ClickAwayListener>
      )}
    </DropdownWrapper>
  );
}

function DefaultButtonContent({ text }: { text: string | ReactNode }) {
  const intl = useIntl();
  return (
    <>
      <span className="max-w-38 overflow-hidden text-label-lg font-medium text-nowrap overflow-ellipsis md:max-w-19 lg:max-w-50">
        {text}
      </span>
      <UserIcon size="xxl">
        <UserCircle tint="cod" title={intl.formatMessage(messages.profile)} />
      </UserIcon>
    </>
  );
}

function TripsContextButtonContent({ isSignedIn }: { isSignedIn: boolean }) {
  return (
    <UserIcon size="xxl">
      <Profile
        className={tw("text-icon-primary", isSignedIn && "text-fill-brand")}
      />
    </UserIcon>
  );
}

const DropdownWrapper = styled.div`
  position: relative;
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  margin: ${spacing.xl};
`;

const ButtonStyles = {
  default: css`
    border-radius: ${border_radius.rounded_xxl};
    padding: ${spacing.sm} ${spacing.md} ${spacing.sm} ${spacing.lg};
    &:hover {
      background: ${color.bg.surface.primaryHover};
    }
  `,
  signedIn: css`
    background: transparent;
    color: ${color.primitive.neutral1400};
    border: 1px solid ${color.primitive.neutral700};
  `,
  horizontalSearch: css`
    border-radius: ${border_radius.rounded_lg};
    background: transparent;
    padding: ${spacing.sm};

    span {
      color: ${color.text.primary};
    }

    &:hover {
      background-color: ${color.primitive.neutral700};
    }
  `,
};

const DropdownButton = styled.button<{
  fullWidth?: boolean;
  $variant: "default" | "horizontalSearch";
  $isSignedIn: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing.md};
  cursor: pointer;

  ${(props) => {
    if (props.fullWidth) {
      return `
        width: 100%;
        justify-content: center;
      `;
    }
  }}

  ${({ $variant }) => ButtonStyles[$variant]}
  ${({ $isSignedIn }) => $isSignedIn && ButtonStyles.signedIn}
`;

const UserIcon = styled(Icon)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledDropdown = styled(Dropdown)`
  min-width: 360px;
  padding-top: ${spacing.xxxl};
  padding-bottom: ${spacing.xl};
  padding-left: ${spacing.xxxl};
  padding-right: ${spacing.xxxl};

  border-radius: ${border_radius.rounded_md};
  top: calc(100% + ${spacing.lg});
`;
